import mixpanel, { Dict } from 'mixpanel-browser'
import { MIXPANEL_TOKEN } from 'src/config'
import { ScheduledVideoCallReason } from 'src/graphql.generated'
import type {
  MarkAsUnreadEntryType,
  MarkAsUnreadProps,
  ParticipantListLayoutAction,
  ParticipantListLayoutChangedProps,
  ParticipantListViewedProps,
  ParticipantOpenedProps,
  PriorityFilterToggledProps,
  RoleFilterChangedProps,
  TagsFilterChangedProps,
} from 'src/participantsHome/participantListAnalytics'
import { getChatSendProps, getParticipantListSnapshot } from './participantListContext'

export enum Event {
  LOGGED_IN = 'logged_in',
  LOGGED_OUT = 'logged_out',
  NAVIGATION = 'navigation',
  TAB_ACTIVITY = 'tab_activity',
  TAB_CLOSED = 'tab_closed',
  USER_ACTIVITY_IN_TAB = 'user_activity_in_tab',
  CHAT_INPUT_FOCUS = 'chat_input_focus',
  CHAT_SEND = 'chat_send',
  TAG_ADDED = 'tag_added',
  TAG_REMOVED = 'tag_removed',
  UPDATE_PROGRAM_START_DATE = 'update_program_start_date',
  TOGGLE_WEEK_VIEW = 'toggle_week_view',
  TOGGLE_GROUP_VIEW = 'toggle_group_view',
  RESTART_PROGRAM = 'restart_program',
  TRIGGER_CHECKIN_CALL_FLOW = 'trigger_checkin_call_flow',
  CREATE_CHECKIN_CALL_APPOINTMENT = 'create_checkin_call_appointment',
  START_INTAKE_CALL = 'start_intake_call',
  START_CHECK_IN_CALL = 'start_check_in_call',
  CANCEL_CHECK_IN_CALL = 'cancel_check_in_call',
  ENABLE_DISABLE_CALL_BOOKING = 'enable_disable_call_booking',
  PARTICIPANT_LIST_VIEWED = 'participant_list_viewed',
  PRIORITY_FILTER_TOGGLED = 'priority_filter_toggled',
  TAGS_FILTER_CHANGED = 'tags_filter_changed',
  ROLE_FILTER_CHANGED = 'role_filter_changed',
  PARTICIPANT_OPENED = 'participant_opened',
  MARK_AS_UNREAD = 'mark_as_unread',
  PARTICIPANT_LIST_LAYOUT_CHANGED = 'participant_list_layout_changed',
}

const track = (event: Event, props?: Dict) =>
  mixpanel.track(event, {
    ...props,
    host: window.location.host,
  })

export const init = (tabID: string) => {
  mixpanel.init(MIXPANEL_TOKEN, {
    // eslint-disable-next-line camelcase
    property_blacklist: [
      '$current_url', // blacklist because url might contain user data (name, email) in the search query
    ],
  })
  mixpanel.register({
    tabID,
  })
}

type LoginData = {
  uid: string
  email?: string | null
  isAdmin?: boolean | null
  isCoach?: boolean | null
  isManager?: boolean | null
  isCareCoordinator?: boolean | null
  isCommunityFacilitator?: boolean | null
  name?: string | null
}

export const trackLogin = (loginData: LoginData) => {
  const { uid, email, isAdmin, isCareCoordinator, isCoach, isCommunityFacilitator, isManager, name } = loginData
  mixpanel.register({
    coachID: uid,
  })
  mixpanel.identify()
  mixpanel.people.set({
    coachID: uid,
    isAdmin,
    isCareCoordinator,
    isCoach,
    isCommunityFacilitator,
    isManager,
    email,
    name,
  })
  track(Event.LOGGED_IN)
}

export const trackLogout = () => track(Event.LOGGED_OUT)

export const trackNavigation = (pathname: string) => track(Event.NAVIGATION, { pathname })

export const trackTabActivity = (isTabActive: boolean) => track(Event.TAB_ACTIVITY, { isTabActive })

export const trackTabClosed = () => track(Event.TAB_CLOSED)

export const trackUserActivityInTab = (isUserActiveInTab: boolean) =>
  track(Event.USER_ACTIVITY_IN_TAB, { isUserActiveInTab })

export const trackChatInputFocus = (isChatInputFocused: boolean) =>
  track(Event.CHAT_INPUT_FOCUS, { isChatInputFocused })

export const trackChatSend = () => track(Event.CHAT_SEND, getChatSendProps())

export const trackAddParticipantTag = () => track(Event.TAG_ADDED)

export const trackRemoveParticipantTag = () => track(Event.TAG_REMOVED)

export const trackProgrmaStartDateUpdate = (weeksMoved: number) =>
  track(Event.UPDATE_PROGRAM_START_DATE, { weeksMoved })

export const trackToggleView = (view: Event.TOGGLE_WEEK_VIEW | Event.TOGGLE_GROUP_VIEW) => track(view)

export const trackRestartProgram = (restartedOnWeek: number | null) => track(Event.RESTART_PROGRAM, { restartedOnWeek })

export const trackTriggerCheckinCallFlow = () => track(Event.TRIGGER_CHECKIN_CALL_FLOW)

export const trackCreateCheckinCallAppointment = (callReason: ScheduledVideoCallReason) =>
  track(Event.CREATE_CHECKIN_CALL_APPOINTMENT, { callReason })

export const trackStartIntakeCall = (meetingID: string) => track(Event.START_INTAKE_CALL, { meetingID })

export const trackStartCheckInCall = (meetingID: string) => track(Event.START_CHECK_IN_CALL, { meetingID })

export const trackCancelCheckInCall = (meetingID: string) => track(Event.CANCEL_CHECK_IN_CALL, { meetingID })

export const trackEnableOrDisableInProgramCallBooking = (props: {
  action: 'enable' | 'disable'
  email: string
  name: string
}) => track(Event.ENABLE_DISABLE_CALL_BOOKING, props)

export const trackParticipantListViewed = (props: ParticipantListViewedProps) =>
  track(Event.PARTICIPANT_LIST_VIEWED, props)

export const trackPriorityFilterToggled = (props: PriorityFilterToggledProps) =>
  track(Event.PRIORITY_FILTER_TOGGLED, props)

export const trackTagsFilterChanged = (props: TagsFilterChangedProps) => track(Event.TAGS_FILTER_CHANGED, props)

export const trackRoleFilterChanged = (props: RoleFilterChangedProps) => track(Event.ROLE_FILTER_CHANGED, props)

export const trackParticipantOpened = (props: ParticipantOpenedProps) => track(Event.PARTICIPANT_OPENED, props)

/**
 * Only tracked while the new participant list UI is shown — that is what publishes the
 * triage context this event's properties come from.
 */
export const trackMarkAsUnread = (entryType: MarkAsUnreadEntryType) => {
  const filterProps = getParticipantListSnapshot()?.filterProps
  if (filterProps) {
    // eslint-disable-next-line camelcase
    const props: MarkAsUnreadProps = { ...filterProps, entry_type: entryType }
    track(Event.MARK_AS_UNREAD, props)
  }
}

/**
 * One event per click on the list's show/hide and expand/shrink controls. Gated like
 * `mark_as_unread`: the filter context comes from the triage snapshot, which is only
 * published once the list has data.
 */
export const trackParticipantListLayoutChanged = (action: ParticipantListLayoutAction) => {
  const filterProps = getParticipantListSnapshot()?.filterProps
  if (filterProps) {
    const props: ParticipantListLayoutChangedProps = { ...filterProps, action }
    track(Event.PARTICIPANT_LIST_LAYOUT_CHANGED, props)
  }
}
